
import {
  CreateBookingV4,
  ErrorBookingV4,
  BookingTabV4,
  AddressTypeV4
} from "@/domain/entities/BookingV4";
import {
  CustomerBookingV3,
  ConfigValidationForm
} from "@/domain/entities/CreateBookingV3";
import { Options, prop, Vue } from "vue-class-component";
import Checkbox from "primevue/checkbox";
import { BookingController } from "@/app/ui/controllers/BookingController";
import debounce from "lodash/debounce";
import {
  receiverNameSchema,
  receiverPhoneSchema,
  receiverSenderAddressSchema,
  receiverCountrySchema,
  getReceiverDestinationSchema,
  receiverPostalCodeSchema
} from "./validation";
import {
  POSTAL_CODE_MAX_LENGTH,
  finalizePostalCode,
  formatPostalCode
} from "./format-postal-code";
import {
  parseMultiFieldZodError,
  parseZodError,
  validationCurrentIndex
} from "../../../create-booking-v3/utils";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";
import { removeNewLineWhitespace } from "@/app/infrastructures/misc/Utils";
import {
  CountryData,
  DistrictData,
  ZipCodeEntities
} from "@/domain/entities/Location";
import { OptionsClass } from "@/domain/entities/MainApp";
import { CustomerListEntities } from "@/domain/entities/Booking";
import { countries as COUNTRIES } from "@/app/infrastructures/misc/BulkData";
import { shouldSkipPhoneValidation } from "../../../booking-utils";
import { PropType } from "vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { parseRecipientText } from "../../helpers/recipient-paste-parser";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { AccountController } from "@/app/ui/controllers/AccountController";

type CustomerParams = {
  search: string;
  isReceiver?: boolean;
};

class Props {
  modelValue = prop<CreateBookingV4>({
    default: new CreateBookingV4(),
    type: Object
  });
  errors = prop<ErrorBookingV4>({
    default: new ErrorBookingV4(),
    type: Object
  });
  currentIndex = prop<number>({
    default: -1,
    type: Number
  });
  receiverCountryIndex = prop<number>({
    default: 5,
    type: Number
  });
  receiverNameIndex = prop<number>({
    default: 6,
    type: Number
  });
  receiverPhoneIndex = prop<number>({
    default: 7,
    type: Number
  });
  receiverAddressIndex = prop<number>({
    default: 8,
    type: Number
  });
  receiverDistrictIndex = prop<number>({
    default: 9,
    type: Number
  });
  receiverPostalCodeIndex = prop<number>({
    default: 10,
    type: Number
  });
  isForeign = prop<boolean>({
    default: false,
    type: Boolean
  });
  isErrorEmbargo = prop<boolean>({
    default: false,
    type: Boolean
  });
  isErrorRouteNotFound = prop<boolean>({
    default: false,
    type: Boolean
  });
  configName = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configPhone = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configAddress = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configDistrict = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configCountry = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configPostalCode = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  preserveDistrictOnPostalClear = prop<boolean>({
    default: false,
    type: Boolean
  });
  syncLocationOnClear = prop<boolean>({
    default: false,
    type: Boolean
  });
  isPostalCodeRequired = prop<boolean>({
    default: true,
    type: Boolean
  });
  isDisableReceiver = prop<boolean>({
    default: false,
    type: Boolean
  });
  allowPaste = prop<boolean>({
    default: true,
    type: Boolean
  });
  isClient = prop<boolean>({ default: false, type: Boolean });
  excludeCountryMatch = prop<boolean>({ default: false, type: Boolean });
}

@Options({
  name: "ReceiverSectionV4",
  components: {
    Checkbox
  },
  emits: [
    "update:modelValue",
    "update:errors",
    "update:currentIndex",
    "update:isErrorRouteNotFound",
    "validationChange"
  ],
  watch: {
    "modelValue.receiver": {
      deep: true,
      handler() {
        this.emitValidationChange();
      }
    },
    errors: {
      deep: true,
      handler() {
        this.emitValidationChange();
      }
    },
    "modelValue.receiver.destinationCountryCode": {
      handler() {
        this.onUpdateDestinationCountryDetail();
      }
    }
  }
})
class ReceiverSectionV4 extends Vue.with(Props) {
  async mounted() {
    this.getCustomerList();
    this.emitValidationChange();
    await this.getCountryList();
    this.onUpdateDestinationCountryDetail();
  }

  get configValidationForm() {
    return ConfigValidationForm;
  }

  get disabledName(): boolean {
    return this.configName.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledPhone(): boolean {
    return this.configPhone.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledAddress(): boolean {
    return this.configAddress.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledDistrict(): boolean {
    const isMalaysiaSelected =
      this.isInternational && this.selectedCountry?.code === "MY";
    return (
      this.configDistrict.rule.includes(ConfigValidationForm.NON_EDITABLE) ||
      isMalaysiaSelected
    );
  }

  get disabledDestinationCountry(): boolean {
    return this.configCountry.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledPostalCode(): boolean {
    return this.configPostalCode.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get isInternational(): boolean {
    return this.modelValue.pageMeta.activeTab === BookingTabV4.INTERNATIONAL;
  }

  get isDestinationCountryEmpty(): boolean {
    return (
      this.isInternational && !this.modelValue.receiver.destinationCountryCode
    );
  }

  // Kecamatan-Kota/Kota-Wilayah label & order — PRD §3.3.7
  get isDistrictFirst(): boolean {
    return !this.isInternational;
  }
  get districtFieldLabel(): string {
    return this.$t(
      this.isDistrictFirst
        ? "bookingV4.recipient.city.label"
        : "bookingV4.recipient.city.labelInternational"
    );
  }
  get districtFieldPlaceholder(): string {
    return this.$t(
      this.isDistrictFirst
        ? "bookingV4.recipient.city.placeholder"
        : "bookingV4.recipient.city.placeholderInternational"
    );
  }

  // receiver name
  get loadingCustomerList(): boolean {
    return BookingController.isLoadingCustomer;
  }
  get customerList() {
    return BookingController.recipientCustomerList.data || [];
  }
  get customerRef() {
    return {
      refType:
        !AccountController.accountData.isPosAccount &&
        this.isClient &&
        (this.modelValue.sender.clientId ||
          AccountController.accountData.account_type_detail.id)
          ? "client"
          : "",
      refId: AccountController.accountData.isPosAccount
        ? 0
        : this.modelValue.sender.clientId ||
          AccountController.accountData.account_type_detail.id ||
          0
    };
  }
  fetchCustomer = debounce((search: string) => {
    this.getCustomerList({
      search,
      isReceiver: true
    });
  }, 1000);
  getCustomerList(params?: CustomerParams) {
    BookingController.getCustomerList({
      ...params,
      cache: true,
      search: params?.search || "",
      refType: this.customerRef.refType,
      refId: Number(this.customerRef.refId || 0)
    });
  }
  onChangeName(value: string) {
    const trimmed = value.trim();
    this.modelValue.receiver.name = trimmed;
    this.onValidateName();
    this.emitModelValue();

    if (!trimmed || !this.errorMessageName) {
      this.fetchCustomer(trimmed);
    }
  }
  onFocusCustomer(index: number, search: string) {
    this.onSelectCurrentIndex(index);
    this.getCustomerList({
      search,
      isReceiver: true
    });
  }
  // Active destination country for the location autofill comparison —
  // International: the country the user selected in the destination picker.
  // Domestic: the implicit origin country used for the destination district
  // lookup (same SSOT as fetchDistrictList, via sender.originCountryCode).
  get activeDestinationCountry(): string {
    return this.isInternational
      ? this.modelValue.receiver.destinationCountryCode
      : this.modelValue.sender.originCountryCode;
  }

  async onSelectCustomer(item: CustomerBookingV3) {
    if (!item) return false;

    if (item.customerDistrictId) {
      const detail = await this.fetchDistrictDetail(
        String(item.customerDistrictId)
      );

      // Only autofill location fields (district + postal code) when the
      // customer's district belongs to the currently active destination
      // country. On mismatch keep the country-agnostic fields (name, phone,
      // address, office flag) and leave district/postal empty so the Zod
      // validation requires a manual destination-region selection. The active
      // destination country is never overwritten from the customer.
      const countryMatches =
        !!detail &&
        !!this.activeDestinationCountry &&
        detail.countryCode === this.activeDestinationCountry;

      const excludeCountryCode = this.excludeCountryMatch && !!detail;

      if (countryMatches || excludeCountryCode) {
        this.modelValue.receiver.destinationCity = detail;
        this.fetchDestinationCity("");

        const postalCodeListOptions = this.buildPostalCodeOptions(detail);
        LocationController.zipCodeData.zipCodeData = postalCodeListOptions;
        this.setPostalCodeDestination(detail, postalCodeListOptions);
        await this.$nextTick();
        this.onValidateDestinationCity();

        if (!this.modelValue.receiver.destinationCountryCode) {
          this.modelValue.receiver.destinationCountryCode = detail.countryCode;
        }
      }
    }

    this.modelValue.receiver.id = item.customerId;
    this.modelValue.receiver.name = item.customerName;
    this.modelValue.receiver.phoneCode = item.customerPhoneCode;
    this.modelValue.receiver.phone = item.customerPhoneNumber;
    this.modelValue.receiver.address = item.customerAddress;
    this.modelValue.receiver.phoneFlag = item.customerPhoneflag;
    const customerAddressType = String(item.customerAddressType)
      .trim()
      .toLowerCase();
    this.modelValue.receiver.addressType = new OptionsClass({
      value: [AddressTypeV4.OFFICE, "kantor", "true"].includes(
        customerAddressType
      )
        ? AddressTypeV4.OFFICE
        : AddressTypeV4.HOME
    });

    this.onValidateName();
    this.onValidatePhone();
    this.onValidateAddress();
    this.emitModelValue();
    this.emitErrors();
  }
  get errorMessageName(): string {
    if (validationCurrentIndex(this.receiverNameIndex, this.currentIndex)) {
      return this.onValidateName();
    }

    return this.errors.receiver;
  }
  onValidateName() {
    const message = parseZodError(
      receiverNameSchema,
      this.modelValue.receiver.name
    );
    this.errors.receiver = message;
    this.emitErrors();

    return message;
  }

  // receiver phone number
  get errorMessagePhone(): string {
    if (validationCurrentIndex(this.receiverPhoneIndex, this.currentIndex)) {
      return this.onValidatePhone();
    }

    return this.errors.phoneReceiver;
  }
  onValidatePhone() {
    if (shouldSkipPhoneValidation(this.fullPhoneNumber, false)) {
      this.errors.phoneReceiver = "";
      this.emitErrors();
      return "";
    }

    const message = parseZodError(receiverPhoneSchema, this.fullPhoneNumber);
    this.errors.phoneReceiver = message;
    this.emitErrors();

    return message;
  }
  get fullPhoneNumber() {
    const { phoneCode, phone } = this.modelValue.receiver;
    return phone ? `${phoneCode}${phone}`.replace("+", "") : phone;
  }
  onChangePhone() {
    this.onValidatePhone();
    this.emitModelValue();

    if (!this.errorMessagePhone) {
      this.fetchCustomer(this.fullPhoneNumber);
    }
  }

  // receiver address
  get errorMessageAddress(): string {
    if (
      validationCurrentIndex(this.receiverAddressIndex, this.currentIndex) ||
      this.modelValue.receiver.address
    ) {
      return this.onValidateAddress();
    }

    return this.errors.addressReceiver;
  }
  onValidateAddress() {
    const message = parseMultiFieldZodError(
      receiverSenderAddressSchema,
      {
        addressReceiver: this.modelValue.receiver.address,
        addressSender: this.modelValue.sender.address
      },
      "addressReceiver"
    );
    this.errors.addressReceiver = message;
    this.emitErrors();

    return message;
  }
  get maxAddressLength() {
    return FlagsMedusa.config_max_address.getValue();
  }
  replaceNewLine(value: string) {
    const removedNewLineAddress = removeNewLineWhitespace(value, " ");

    this.modelValue.receiver.address = removedNewLineAddress;
    this.emitModelValue();

    return removedNewLineAddress;
  }

  // office flag
  get isOfficeFlag(): boolean {
    return this.modelValue.receiver.addressType?.value === AddressTypeV4.OFFICE;
  }
  set isOfficeFlag(value: boolean) {
    this.modelValue.receiver.addressType = new OptionsClass({
      value: value ? AddressTypeV4.OFFICE : AddressTypeV4.HOME
    });
    this.emitModelValue();
  }

  // negara tujuan (international only)
  // TODO(follow-up ticket): `countries` (BulkData.ts) is a phone-dial-code list
  // ("+62", not "ID"), reused here for lack of a wired-in ISO country picker.
  // `destinationCountryCode` is read as an ISO code elsewhere (see CargoV2.ts).
  // Swap to LocationController.getCountryList()/CountryData once that search+
  // pagination UX is available for this field.
  get selectedCountry(): CountryData | null {
    return (
      this.countryList.find(
        (item) => item.code === this.modelValue.receiver.destinationCountryCode
      ) || null
    );
  }
  set selectedCountry(value: CountryData | null) {
    const previousCountryCode = this.modelValue.receiver.destinationCountryCode;
    this.modelValue.receiver.destinationCountryCode = value?.code || "";

    if (
      previousCountryCode !== this.modelValue.receiver.destinationCountryCode
    ) {
      this.modelValue.receiver.destinationCity = "";
      this.modelValue.receiver.postalCode = "";
      this.dataListDistrict = [];
      LocationController.zipCodeData.zipCodeData = [];
    }

    this.onValidateCountry();
    this.emitModelValue();
  }
  onChangeDestinationCountry(value: string | CountryData) {
    this.selectedCountry = value as CountryData;
    this.modelValue.receiver.phoneCode = this.selectedCountry?.phoneCode || "";
    this.$emit("change-country", this.selectedCountry);
  }
  get errorMessageCountry(): string {
    if (!this.isInternational) return "";

    if (validationCurrentIndex(this.receiverCountryIndex, this.currentIndex)) {
      return this.onValidateCountry();
    }

    return this.errors.destinationCountry;
  }
  onValidateCountry() {
    if (!this.isInternational) {
      this.errors.destinationCountry = "";
      this.emitErrors();
      return "";
    }

    const message = parseZodError(
      receiverCountrySchema,
      this.modelValue.receiver.destinationCountryCode
    );
    this.errors.destinationCountry = message;
    if (message) {
      this.onValidateDestinationCity();
      this.onValidatePostalCode();
    }
    this.emitErrors();

    return message;
  }

  // destination district
  get destinationCity(): DistrictData {
    if (typeof this.modelValue.receiver.destinationCity === "string") {
      return new DistrictData();
    }
    return this.modelValue.receiver.destinationCity;
  }
  get showMisbookingGuidance(): boolean {
    return this.destinationCity?.isMisbookingPotential === "yes";
  }
  get errorMessageDestination(): string {
    if (validationCurrentIndex(this.receiverDistrictIndex, this.currentIndex)) {
      return this.onValidateDestinationCity();
    }
    return this.errors.destinationCity;
  }
  onValidateDestinationCity() {
    const message = this.isDestinationCountryEmpty
      ? "bookingV4.recipient.city.error.requiredInternational"
      : parseZodError(
          getReceiverDestinationSchema(this.isDistrictFirst),
          this.destinationCity?.name || ""
        );
    this.errors.destinationCity = message;
    this.emitErrors();

    return message;
  }
  get isLoadingDestinationCity() {
    return LocationController.isLoadingDistrict;
  }
  dataListDistrict: DistrictData[] = [];
  currentPageDistrict = 1;
  resetDestinationDistrictOptions(): void {
    this.dataListDistrict = [];
    this.currentPageDistrict = 1;
    LocationController.zipCodeData.zipCodeData = [];
  }
  get destinationCityList(): DistrictData[] {
    return this.dataListDistrict
      .filter((item: DistrictData) => item.status.toLowerCase() === "active")
      .map(
        (e: DistrictData) =>
          new DistrictData({
            ...e,
            name: `${e.name}, ${e.cityName}`,
            countryCode: "ID"
          })
      );
  }
  async fetchDistrictList(search: string, page = 1): Promise<DistrictData[]> {
    return await LocationController.getDistrictList({
      search,
      status: "active",
      page,
      limit: 20,
      excludeCountry: "",
      type: "",
      cache: true,
      countryCode: this.isInternational
        ? this.modelValue.receiver.destinationCountryCode
        : this.modelValue.sender.originCountryCode
    });
  }
  fetchDestinationCity = debounce(async (search: string) => {
    if (this.isDestinationCountryEmpty) {
      this.dataListDistrict = [];
      this.onValidateDestinationCity();
      return;
    }

    this.currentPageDistrict = 1;
    const data = await this.fetchDistrictList(search, 1);
    this.dataListDistrict = data;
  }, 250);
  async onScrollDistrict(search: string) {
    const nextPage = this.currentPageDistrict + 1;
    const data = await this.fetchDistrictList(search, nextPage);

    this.dataListDistrict = [...this.dataListDistrict, ...data];
    this.currentPageDistrict = nextPage;
  }
  onFocusDestinationCity() {
    this.onSelectCurrentIndex(this.receiverDistrictIndex);

    if (this.isDestinationCountryEmpty) {
      this.onValidateDestinationCity();
      return;
    }

    // if (this.errorMessageDestination) return;
    this.onGetDestinationCity();
  }
  onGetDestinationCity() {
    if (
      !this.destinationCityList.length ||
      !this.modelValue.receiver.destinationCity
    ) {
      LocationController.setLoadingDistrict(true);
      this.fetchDestinationCity("");
    }
  }
  async onSelectDestinationCity() {
    if (this.isDestinationCountryEmpty) {
      this.onValidateDestinationCity();
      return;
    }

    const { id } = this.destinationCity;
    if (!id) {
      if (this.syncLocationOnClear) this.clearSearch();
      return;
    }

    const detail = await this.fetchDistrictDetail(String(id));

    const postalCodeListOptions = this.buildPostalCodeOptions(detail);
    LocationController.zipCodeData.zipCodeData = postalCodeListOptions;
    await this.$nextTick();
    this.modelValue.receiver.destinationCity = new DistrictData({
      ...detail,
      name: `${detail.name}, ${detail.cityName}`
    });
    if (!this.isInternational) {
      this.modelValue.receiver.destinationCountryCode = detail.countryCode;
    }

    this.setPostalCodeDestination(detail, postalCodeListOptions);
    this.onValidateDestinationCity();
    this.emitModelValue();
    this.onValidateCountry();
  }
  clearSearch() {
    this.modelValue.receiver.destinationCity = "";
    if (this.syncLocationOnClear) {
      this.modelValue.receiver.postalCode = "";
      this.onValidatePostalCode();
    }
    this.emitModelValue();
    this.onValidateDestinationCity();

    this.$emit("update:isErrorRouteNotFound", false);
  }
  clearPostalCode() {
    // GENESIS-23378/23385 — Fixing ini mungkin impact ke Booking CA, Tokopedia,
    // dan C1. Minta tolong cek kecamatan disabled saat kode pos dihapus.
    if (this.shouldPreserveDistrictOnPostalChange) return;
    if (this.syncLocationOnClear || !this.preserveDistrictOnPostalClear) {
      this.clearSearch();
    }
  }
  async fetchDistrictDetail(id: string) {
    return await LocationController.getDetailDistrict({
      id,
      notSaveDetail: true
    });
  }

  get vendorTypeForDestinationDistrict() {
    return ["vendor", "vendor_lanjutan"].includes(
      this.destinationCity?.type?.toLowerCase() ?? ""
    );
  }
  get vendorCodeForDestinationDistrict() {
    return ["ninja", "nx", "pi"].includes(
      this.destinationCity?.vendorCode?.toLowerCase() ?? ""
    );
  }
  get isVendorNinjaDestination(): boolean {
    const res =
      this.vendorTypeForDestinationDistrict &&
      this.vendorCodeForDestinationDistrict &&
      this.modelValue.pieces.length >= 1;

    this.modelValue.receiver.isVendorNinjaDestination = res;

    return res;
  }

  // postal code
  buildPostalCodeOptions(detail: any): DistrictData[] {
    const { originZipCode } = this.destinationCity;
    const postalCodeData: string[] = detail.destinationListZipCode.length
      ? detail.destinationListZipCode
      : originZipCode
      ? [originZipCode]
      : [];

    return postalCodeData.map(
      (value) =>
        new DistrictData({
          ...detail,
          name: value,
          destinationZipCode: value,
          districtName: detail.name
        })
    );
  }
  setPostalCodeDestination(detail: any, options: DistrictData[]) {
    const { originZipCode } = this.destinationCity;
    const postalCode = detail.destinationZipCode || originZipCode;
    if (!postalCode || !options.length) return;
    const selectedPostalCode = originZipCode || postalCode.split(",")[0].trim();

    const postalCodeFromDestination = new DistrictData({
      ...detail,
      name: selectedPostalCode,
      destinationZipCode: selectedPostalCode,
      districtName: detail.name
    });

    this.modelValue.receiver.postalCode = postalCode.includes(",")
      ? originZipCode
        ? postalCodeFromDestination
        : options[0]
      : postalCodeFromDestination;

    this.onValidatePostalCode();
  }

  // postal code
  get postalCode(): DistrictData {
    if (typeof this.modelValue.receiver.postalCode === "string") {
      return new DistrictData();
    }
    return this.modelValue.receiver.postalCode;
  }
  get errorMessagePostalCode(): string {
    if (
      validationCurrentIndex(this.receiverPostalCodeIndex, this.currentIndex)
    ) {
      return this.onValidatePostalCode();
    }

    return this.errors.postalCode;
  }
  onBlurPostalCode() {
    const postalCode = this.modelValue.receiver.postalCode;
    if (!(postalCode instanceof DistrictData)) {
      this.onValidatePostalCode();
      return;
    }
    const trimmedName = finalizePostalCode(postalCode.name.trim());
    if (trimmedName !== postalCode.name) {
      this.modelValue.receiver.postalCode = new DistrictData({
        ...postalCode,
        name: trimmedName
      });
      this.emitModelValue();
    }
    this.onValidatePostalCode();
  }
  onValidatePostalCode() {
    const postalCode = this.postalCode?.name || "";
    const message =
      !this.isPostalCodeRequired && !postalCode
        ? ""
        : this.isDestinationCountryEmpty
        ? "bookingV4.recipient.postalCode.error.required"
        : parseZodError(receiverPostalCodeSchema, postalCode);
    this.errors.postalCode = message;
    this.emitErrors();

    return message;
  }
  get postalCodeList() {
    return LocationController.zipCodeData.zipCodeData;
  }
  get shouldPreserveDistrictOnPostalChange(): boolean {
    return (
      this.preserveDistrictOnPostalClear &&
      (this.disabledDistrict || this.isDisableReceiver)
    );
  }
  allowAlphanumeric(event: KeyboardEvent) {
    const isAllowedChar = /^[A-Za-z0-9- ]$/;
    const input = event.target as HTMLInputElement;

    if (!isAllowedChar.test(event.key)) {
      event.preventDefault();
    }

    if (input.value.length >= POSTAL_CODE_MAX_LENGTH) {
      event.preventDefault();
    }
  }
  onGetPostalCodeList(search: string) {
    const typedValue = formatPostalCode(search);
    const sanitizedSearch = finalizePostalCode(typedValue);
    if (!sanitizedSearch && this.syncLocationOnClear) {
      this.clearPostalCode();
    }
    this.modelValue.receiver.postalCode = sanitizedSearch
      ? new DistrictData({
          id: /^\d+$/.test(sanitizedSearch) ? Number(sanitizedSearch) : 0,
          name: typedValue,
          destinationZipCode: sanitizedSearch
        })
      : "";
    this.onValidatePostalCode();
    this.emitModelValue();

    if (this.isDestinationCountryEmpty) {
      LocationController.zipCodeData.zipCodeData = [];
      return;
    }

    this.fetchPostalCodeList(sanitizedSearch, this);
  }

  fetchPostalCodeList = debounce(async (search: string, _this = this) => {
    const response: ZipCodeEntities = await LocationController.getZipCodeList({
      search: search,
      page: 1,
      limit: 10,
      countryCode: _this.isInternational
        ? _this.modelValue.receiver.destinationCountryCode
        : _this.modelValue.sender.originCountryCode
    });
    const data = response.zipCodeData.map(
      (item: DistrictData) =>
        new DistrictData({
          ...item,
          name: item.originZipCode + " - " + item.name + ", " + item.cityName,
          destinationZipCode: item.originZipCode,
          districtName: item.name
        })
    );
    LocationController.setZipCodeData(
      new ZipCodeEntities(response.pagination, data)
    );
  }, 250);

  get eligibleAutofillDistrict() {
    return (
      !this.destinationCity?.originZipCode &&
      this.modelValue.receiver.postalCode
    );
  }
  async onChangePostalCode(value: string | DistrictData) {
    if (!value && this.syncLocationOnClear) {
      this.clearPostalCode();
      return;
    }
    if (this.isDestinationCountryEmpty) {
      this.onValidatePostalCode();
      this.emitModelValue();
      return;
    }

    const hadPostalCodeBefore = !!this.postalCode?.name;

    if (
      !value &&
      !this.shouldPreserveDistrictOnPostalChange &&
      hadPostalCodeBefore
    ) {
      this.modelValue.receiver.destinationCity = "";
    }

    if (
      !this.shouldPreserveDistrictOnPostalChange &&
      !this.configDistrict.rule.includes(ConfigValidationForm.NON_EDITABLE) &&
      (this.eligibleAutofillDistrict ||
        (value as DistrictData)?.name?.includes("-"))
    ) {
      const detail = await this.fetchDistrictDetail(String(this.postalCode.id));

      const { districtName, cityName } = this.postalCode;

      this.modelValue.receiver.destinationCity = new DistrictData({
        ...this.postalCode,
        name: districtName + ", " + cityName,
        countryCode: detail.countryCode,
        isMisbookingPotential: detail.isMisbookingPotential
      });

      if (!this.isInternational) {
        this.modelValue.receiver.destinationCountryCode = detail.countryCode;
      }
      await this.$nextTick();
      const postalCodeListOptions = this.buildPostalCodeOptions(detail);
      LocationController.zipCodeData.zipCodeData = postalCodeListOptions;
      this.setPostalCodeDestination(detail, postalCodeListOptions);
      this.onValidateDestinationCity();
    }

    this.onValidatePostalCode();
    this.emitModelValue();
  }

  // Salin & Isi Otomatis — clipboard paste parsing (no confirmation dialog)
  isAutofillProcessing = false;
  async onAutofillFromClipboard() {
    if (this.isAutofillProcessing) return;

    this.isAutofillProcessing = true;

    try {
      const clipboardItems = await navigator.clipboard.read();
      const types = clipboardItems[0]?.types || [];

      const hasImage = types.some((type: string) => type.startsWith("image/"));
      const hasText = types.some((type: string) => type.startsWith("text/"));

      if (hasImage || !hasText) {
        this.showErrorSnackbar(
          this.$t("bookingV4.recipient.autofill.unsupportedFormat")
        );
        return;
      }

      const pastedText = await navigator.clipboard.readText();

      if (!pastedText || pastedText.trim() === "") {
        this.showErrorSnackbar(
          this.$t("bookingV4.recipient.autofill.emptyClipboard")
        );
        return;
      }

      this.applyAutofillText(pastedText);
    } catch (error) {
      this.showErrorSnackbar(
        this.$t("bookingV4.recipient.autofill.processingError")
      );
    } finally {
      setTimeout(() => {
        this.isAutofillProcessing = false;
      }, 500);
    }
  }

  // Helper method to keep your code DRY (Don't Repeat Yourself)
  showErrorSnackbar(message: string) {
    MainAppController.setSnackbarMessage(message);
    MainAppController.setSnackbarPositivity(false);
    this.setSnackbar();
  }

  showSuccessSnackbar(message: string) {
    MainAppController.setSnackbarMessage(message);
    MainAppController.setSnackbarPositivity(true);
    this.setSnackbar();
  }

  setSnackbar() {
    MainAppController.setSnackbarTimeout(3000);
    MainAppController.setAutoClose(true);
    MainAppController.setSnackbar(true);
  }

  applyAutofillText(pastedText: string) {
    const parsed = parseRecipientText(pastedText);
    let hasAppliedData = false;

    if (parsed.name && !this.disabledName) {
      this.modelValue.receiver.name = parsed.name;
      this.onValidateName();
      hasAppliedData = true;
    }

    if (parsed.phoneCode && !this.disabledPhone) {
      this.modelValue.receiver.phoneCode = parsed.phoneCode;
    }

    if (parsed.phoneNumber && !this.disabledPhone) {
      this.modelValue.receiver.phone = parsed.phoneNumber;
    }

    if ((parsed.phoneCode || parsed.phoneNumber) && !this.disabledPhone) {
      this.onValidatePhone();
      hasAppliedData = true;
    }

    if (parsed.address && !this.disabledAddress) {
      this.modelValue.receiver.address = parsed.address;
      this.onValidateAddress();
      hasAppliedData = true;
    }

    if (!hasAppliedData) {
      this.showErrorSnackbar(
        this.$t("bookingV4.recipient.autofill.unsupportedFormat")
      );
      return;
    }

    this.emitModelValue();

    this.showSuccessSnackbar(this.$t("bookingV4.recipient.autofill.success"));
  }

  get disabledPasteAutofill() {
    return (
      this.isAutofillProcessing ||
      (this.disabledName && this.disabledPhone && this.disabledAddress)
    );
  }

  // save address
  onSelectCurrentIndex(index: number) {
    setTimeout(() => {
      if (!validationCurrentIndex(index, this.currentIndex)) {
        this.$emit("update:currentIndex", index);
      }
    }, 0);
  }
  async onScroll(search: string) {
    const nextPage = BookingController.senderCustomerList.pagination.page + 1;
    const data = await BookingController.getCustomerInfiniteScrollList({
      cache: true,
      search: search || "",
      refType: this.customerRef.refType,
      refId: Number(this.customerRef.refId || 0),
      isSender: false,
      page: nextPage
    });

    BookingController.setSenderCustomerList(
      new CustomerListEntities(data.pagination, [
        ...this.customerList,
        ...data.data
      ])
    );
  }

  // FormCompletion contract (GENESIS-19583 consumes validationChange)
  emitModelValue() {
    this.$emit("update:modelValue", this.modelValue);
  }
  emitErrors() {
    this.$emit("update:errors", this.errors);
  }
  get isReceiverValid(): boolean {
    const { name, phone, address } = this.modelValue.receiver;
    const districtValid = !!this.destinationCity?.name;
    const postalValid = !this.isPostalCodeRequired || !!this.postalCode?.name;
    const countryValid =
      !this.isInternational ||
      !!this.modelValue.receiver.destinationCountryCode;

    const validForm = [
      !!name,
      !!phone,
      !!address,
      districtValid,
      postalValid,
      countryValid,
      !this.errors.receiver,
      !this.errors.phoneReceiver,
      !this.errors.addressReceiver
    ].every(Boolean);

    if (this.isInternational) {
      return validForm;
    }

    return validForm && !this.errorMessageCountry;
  }
  emitValidationChange() {
    this.$emit("validationChange", this.isReceiverValid);
  }

  countryList = [] as CountryData[];
  get countryListFiltered() {
    return this.countryList.filter(
      (item) => item.code !== this.modelValue.sender.originCountryCode
    );
  }

  async getCountryList() {
    const res = await LocationController.getCountryList({
      search: "",
      status: "active",
      page: 1,
      limit: 250
    });

    if (!res?.countryData) return;

    this.countryList = res.countryData.map((item) => ({
      ...item,
      label: `${item.name} (${item.code})`
    }));
  }

  async onUpdateDestinationCountryDetail() {
    const selectedCountry = this.countryList.find(
      (item) => item.code === this.modelValue.receiver.destinationCountryCode
    ) as CountryData;

    this.modelValue.receiver.destinationCountry = selectedCountry;
  }
}

export default ReceiverSectionV4;
